// Compact colours of GET logos/options/all (API flag
// LogoOptionsCompactColorsEnabled, frontend flag
// REACT_APP_LOGO_OPTIONS_COMPACT_COLORS_ENABLED - LOGOLISTE-LAST 3/10 §3 E2).
// Pure module: no flag reads here; mapLogoOptions passes the flag in.
//
// Prod facts (3/10): the answer is 2,83 MB and the bulk of it is every
// method key repeated per colour in three colour lists. With the API flag on
// the colours arrive with methods: null and the answer says
// colorMethodsCompact: true; the methods are then derived here with the
// SAME rule the server applies (ListLoadPolicy.ColorMethods =
// LogoService.GetAvailableColors):
//   every method of transferMethodsAll,
//   minus those without neon support for a neon colour,
//   minus those without metallic support for a metallic colour,
//   as keys in ordinal order.
// transferMethodsAll is never filtered per user (blocked methods stay in
// it), so the derived list equals the list the full answer carries. 0 calls.

export interface CompactColorMethod {
  key: string;
  withNeon?: boolean;
  withMetallic?: boolean;
}

export interface CompactColorKind {
  isNeon: boolean;
  isMetallic: boolean;
}

export interface CompactColorsAnswer {
  colorMethodsCompact?: boolean | null;
  transferMethodsAll?: CompactColorMethod[] | null;
  transferMethods?: CompactColorMethod[] | null;
  transferMethodsNumbers?: CompactColorMethod[] | null;
  transferMethodsNames?: CompactColorMethod[] | null;
}

// The JSON name of the API's hint (ListLoadPolicy.ColorMethodsCompactJsonName).
export const COLOR_METHODS_COMPACT_HINT = "colorMethodsCompact";

function ordinal(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

// The server's rule. Only an explicit `false` excludes a method: the API
// always sends both booleans, so this equals `color.IsNeon && !m.WithNeon`
// there, and a method that lacks the field fails open (the server validates
// the chosen colour and method independently).
export function deriveColorMethods(
  color: CompactColorKind,
  allMethods: readonly CompactColorMethod[] | null | undefined,
): string[] {
  if (!color || !Array.isArray(allMethods)) return [];
  const neon = color.isNeon === true;
  const metallic = color.isMetallic === true;
  return allMethods
    .filter(
      (m) =>
        !!m &&
        typeof m.key === "string" &&
        (!neon || m.withNeon !== false) &&
        (!metallic || m.withMetallic !== false),
    )
    .map((m) => m.key)
    .sort(ordinal);
}

// The list the rule runs over: transferMethodsAll as the server does. An
// answer without that list (never sent by today's API) fails open to the
// methods the answer does carry, so a colour is never left without methods
// because of a degraded payload.
function sourceMethods(answer: CompactColorsAnswer): CompactColorMethod[] {
  if (Array.isArray(answer.transferMethodsAll)) return answer.transferMethodsAll;
  const seen = new Set<string>();
  const union: CompactColorMethod[] = [];
  for (const list of [
    answer.transferMethods,
    answer.transferMethodsNumbers,
    answer.transferMethodsNames,
  ]) {
    if (!Array.isArray(list)) continue;
    for (const m of list) {
      if (!m || typeof m.key !== "string" || seen.has(m.key)) continue;
      seen.add(m.key);
      union.push(m);
    }
  }
  return union;
}

// Returns the colour mapper for a compact answer, or null when the answer is
// to be used as is: the flag is off, or the answer carries no hint (API flag
// off / older API). A colour that arrives WITH methods keeps the server's
// list. The derived list is computed once per colour kind (plain, neon,
// metallic, both) and shared by every colour of that kind in all three
// lists - frozen, so no consumer can change it for the others.
export function compactColorMapper<
  T extends CompactColorKind & { methods?: string[] | null },
>(
  answer: CompactColorsAnswer | null | undefined,
  enabled: boolean,
): ((color: T) => T & { methods: string[] }) | null {
  if (!enabled || !answer || answer.colorMethodsCompact !== true) return null;
  const all = sourceMethods(answer);
  const byKind = new Map<number, string[]>();
  return (color: T) => {
    if (!color || Array.isArray(color.methods)) {
      return color as T & { methods: string[] };
    }
    const kind =
      (color.isNeon === true ? 1 : 0) + (color.isMetallic === true ? 2 : 0);
    let methods = byKind.get(kind);
    if (!methods) {
      methods = Object.freeze(deriveColorMethods(color, all)) as string[];
      byKind.set(kind, methods);
    }
    return { ...color, methods };
  };
}
